/**
 * Shared tournament phase badge derivation.
 *
 * Consumed by both the admin Fixtures Management panel and the public
 * Fixtures tab so they never drift out of sync.
 *
 * Pass the full fixtures list for the event and receive either `null`
 * (no fixtures yet) or a `{ label, icon, tone, adminTone }` object.
 */

const KNOCKOUT_ROUND_TYPES = [
  'round_of_32', 'round_of_16', 'quarter_final',
  'semi_final', 'third_place', 'final',
];

export function derivePhaseBadge(fixtures = []) {
  const league = fixtures.filter(f => ['league', 'group'].includes(f.round_type));
  const leaguePlayed = league.filter(f => f.match_status === 'completed').length;
  const leagueTotal = league.length;

  const knockouts = fixtures.filter(f => KNOCKOUT_ROUND_TYPES.includes(f.round_type));
  const finalFixture = knockouts.find(f => f.round_type === 'final');
  const knockoutsSeeded = knockouts.some(f => f.team_a_id && f.team_b_id);
  const tournamentComplete = !!finalFixture && finalFixture.match_status === 'completed';

  if (tournamentComplete) {
    return {
      key: 'complete',
      label: 'Tournament Complete',
      icon: 'ph-medal',
      // Tones for dark (admin) and light (public) surfaces
      tone: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
      lightTone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    };
  }
  if (knockoutsSeeded) {
    const activeKn = knockouts.filter(f => f.match_status === 'live').length;
    return {
      key: 'knockouts',
      label: activeKn > 0 ? `Knockouts Live · ${activeKn} in play` : 'Knockouts Live',
      icon: 'ph-tree-structure',
      tone: 'bg-rose-500/20 text-rose-200 border-rose-400/30',
      lightTone: 'bg-rose-50 text-rose-700 border-rose-200',
    };
  }
  if (leagueTotal > 0) {
    const allDone = leaguePlayed === leagueTotal;
    return {
      key: allDone ? 'league_awaiting_close' : 'league_open',
      label: allDone
        ? `League · ${leaguePlayed}/${leagueTotal} played · awaiting knockouts`
        : `League · ${leaguePlayed}/${leagueTotal} played`,
      icon: allDone ? 'ph-clock-countdown' : 'ph-play-circle',
      tone: allDone
        ? 'bg-amber-500/20 text-amber-200 border-amber-400/30'
        : 'bg-blue-500/20 text-blue-200 border-blue-400/30',
      lightTone: allDone
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-blue-50 text-blue-700 border-blue-200',
    };
  }
  return null;
}
